import React from "react";
import { Link } from "react-router-dom";
import { Loader } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../../design-system/tokens";
import { card, PanelHead, CopyButton, InlineValue, Note } from "../../sshProxyUi";
import { formatRelativeAge, orDash, proxyLiveness } from "../../sshProxyModel";

// Overview — the gateway's identity and what it reports on its heartbeat.
// Read-only on purpose: everything here is either set at registration or
// observed from the heartbeat. Nothing on this tab is a control.

function Fact({ label, children, mono = true }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: AN_MONO, fontSize: 9.5, letterSpacing: 0.9, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: mono ? AN_MONO : AN_FONT, fontSize: 12.5, color: AN_COLORS.ink, wordBreak: "break-all" }}>{children}</div>
    </div>
  );
}

function KeyRow({ label, value, pendingNote }) {
  return (
    <div style={{ borderTop: `1px solid ${AN_COLORS.line2}`, paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: label ? "space-between" : "flex-end", gap: 10, marginBottom: 6 }}>
        {label && <span style={{ fontFamily: AN_MONO, fontSize: 11.5, fontWeight: 500, color: AN_COLORS.ink2 }}>{label}</span>}
        {value ? <CopyButton text={value} label="Copy" /> : null}
      </div>
      {value ? (
        <div style={{ fontFamily: AN_MONO, fontSize: 12, background: AN_COLORS.tint, border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, padding: "10px 12px", wordBreak: "break-all", color: AN_COLORS.ink2 }}>
          {value}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, border: `1px dashed ${AN_COLORS.line}`, borderRadius: 7, padding: "10px 12px", background: AN_COLORS.bg, color: AN_COLORS.muted, fontFamily: AN_FONT, fontSize: 12.5 }}>
          <Loader size={14} />
          {pendingNote}
        </div>
      )}
    </div>
  );
}

export default function OverviewTab({ gateway, jumpServerId }) {
  const live = proxyLiveness(gateway?.lastSeenAt);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          eyebrow="Overview"
          title="What the gateway reports"
          desc="Liveness and the values below come from the heartbeat, not from anything set in the console. Administrative status is a separate fact and lives on the gateway list."
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
          <Fact label="Liveness" mono={false}>{live.label}</Fact>
          <Fact label="Last heartbeat">{gateway?.lastSeenAt ? formatRelativeAge((Date.now() - new Date(gateway.lastSeenAt).getTime()) / 1000) : "—"}</Fact>
          {/* Observed server-side, not claimed by the proxy — which is what makes
              it the field that confirms the heartbeat comes from the right box. */}
          <Fact label="Seen from">{orDash(gateway?.lastSeenIp)}</Fact>
          <Fact label="Version">{orDash(gateway?.proxyVersion)}</Fact>
          <Fact label="Listening on">{orDash(gateway?.proxyListen)}</Fact>
          <Fact label="Public IP">{orDash(gateway?.publicIpAddress)}</Fact>
          <Fact label="Private IP">{orDash(gateway?.privateIpAddress)}</Fact>
          <Fact label="Region" mono={false}>{orDash(gateway?.region)}</Fact>
          <Fact label="Gateway id">{orDash(jumpServerId)}</Fact>
        </div>
      </div>

      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          eyebrow="Overview"
          title="Host key fingerprint"
          desc="Operators see this the first time they connect. If their ssh client shows a different one, they should not connect."
        />
        <KeyRow
          value={gateway?.hostKeyFingerprint}
          pendingNote="Waiting for the gateway to report its host-key fingerprint."
        />
      </div>

      {live.state === "never" && (
        <Note tone="info">
          This gateway has never checked in. That is usually <InlineValue>SSHPROXY_CONTROL_URL</InlineValue> being unset
          — SSH itself still works, the console just never hears from the process. It is not the same as offline. Run
          through <Link to="/pam/jumpServer/onboard">onboarding</Link> if the gateway was never installed.
        </Note>
      )}
    </div>
  );
}
